import type { CanvasOverlay } from "../canvas/BuilderCanvas";
import type { DiffStatus } from "../canvas/nodes";
import { ensureIds, clone } from "../graph";
import type { Graph, WfEdge, WfNode } from "../types";

const strip = <T extends { position?: unknown }>(x: T) => { const { position: _p, ...rest } = x as T & { position?: unknown }; return JSON.stringify(rest); };

export interface Comparison {
  status: Map<string, DiffStatus>;
  removedNodes: WfNode[];
  removedEdges: WfEdge[];
  added: number;
  removed: number;
  modified: number;
  moved: number;
}

/** Client-side comparison by stable id (the server's change list adds field-level detail). */
export function compareGraphs(before: Graph, after: Graph): Comparison {
  const a = ensureIds(clone(before));
  const b = ensureIds(clone(after));
  const status = new Map<string, DiffStatus>();
  const bn = new Map(a.nodes.map((n) => [n.id!, n]));
  const be = new Map(a.edges.map((e) => [e.id!, e]));
  let moved = 0;
  for (const n of b.nodes) {
    const old = bn.get(n.id!);
    if (!old) status.set(n.id!, "added");
    else if (strip(old) !== strip(n)) status.set(n.id!, "modified");
    else if (JSON.stringify(old.position) !== JSON.stringify(n.position)) moved++;
  }
  for (const e of b.edges) {
    const old = be.get(e.id!);
    if (!old) status.set(e.id!, "added");
    else if (JSON.stringify(old) !== JSON.stringify(e)) status.set(e.id!, "modified");
  }
  const afterNodes = new Set(b.nodes.map((n) => n.id));
  const afterEdges = new Set(b.edges.map((e) => e.id));
  const removedNodes = a.nodes.filter((n) => !afterNodes.has(n.id));
  const removedEdges = a.edges.filter((e) => !afterEdges.has(e.id));
  for (const n of removedNodes) status.set(n.id!, "removed");
  for (const e of removedEdges) status.set(e.id!, "removed");
  const values = [...status.values()];
  return { status, removedNodes, removedEdges, added: values.filter((v) => v === "added").length, removed: values.filter((v) => v === "removed").length, modified: values.filter((v) => v === "modified").length, moved };
}

export function overlayOf(c: Comparison): CanvasOverlay {
  return { status: c.status, ghostNodes: c.removedNodes, ghostEdges: c.removedEdges };
}

export const changeCount = (c: Comparison) => c.added + c.removed + c.modified;
